// BadgeCards.tsx
import React, { useEffect, useMemo, useState } from "react";
import { usePost } from "../../../hooks/usePost";
import styled from "styled-components";
import {
  Card,
  ElementContainer,
  RowContainer,
} from "../../../components/containers/styledComponents";
import {
  EngagementType,
  TopicMapping,
} from "../../../models/stewardshipModels";
import { BadgeCard } from "./BadgeCard";
import { formatReadable } from "../../../utils/formattingUtils";
import { LoadingTableCell } from "../../../components/tables/LoadingTableCell";
import { MESSAGES } from "../../../messages";
import { CompassTag } from "../../../components/visuals/CompassTag";
import { ENGAGEMENT_META_THEME_IMAGES } from "../constants";
import { lightGreys, statusBadgeColors } from "../../../styles";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { getBadges } from "../utils";
import {
  AvatarStack,
  AvatarStackProps,
} from "../../../components/visuals/Avatar";

const BadgeCardContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  justify-content: flex-start;
`;

interface AggregatedDataItem
  extends Omit<TopicMapping, "topic_id" | "topic_name"> {
  topics: string[];
}

const MATERIAL_ENGAGEMENT_META_THEMES = Object.values(EngagementType).filter(
  (theme) => theme !== EngagementType.Governance
);

export const BadgeCards = ({ companyId }: BadgeCardsProps): JSX.Element => {
  const {
    sendData: fetchCompaniesEngagementTopicMapping,
    data,
    isLoading,
  } = usePost<TopicMapping[], { compass_cids: string[] }>();

  const [selectedMetaThemes, setSelectedMetaThemes] = useState<
    EngagementType[]
  >(MATERIAL_ENGAGEMENT_META_THEMES);

  useEffect(() => {
    if (companyId) {
      fetchCompaniesEngagementTopicMapping(
        "GET_COMPANIES_ENGAGEMENT_TOPIC_MAPPING",
        { compass_cids: [companyId] }
      );
    }
  }, [companyId]);

  const handleSelectMetaTheme = (metaTheme: EngagementType): void => {
    setSelectedMetaThemes((prev) => {
      const availableMetaThemes = Array.from(
        new Set(data?.map((item) => item.meta_theme))
      );
      const updated = prev.includes(metaTheme)
        ? prev.filter((t) => t !== metaTheme)
        : [...prev, metaTheme];
      const filteredUpdated = updated.filter((t) =>
        availableMetaThemes.includes(t)
      );

      return filteredUpdated.length > 0
        ? filteredUpdated
        : MATERIAL_ENGAGEMENT_META_THEMES;
    });
  };

  const getMetaThemeTagProps = (
    metaTheme: EngagementType,
    selectedMetaThemes: EngagementType[]
  ): { color: string; bg: string } => {
    const isSelected = selectedMetaThemes.includes(metaTheme);
    const colors = ENGAGEMENT_META_THEME_IMAGES[metaTheme];

    return {
      color: isSelected
        ? colors.textBadgeColor ?? statusBadgeColors.Planned.text
        : statusBadgeColors.Planned.text,
      bg: isSelected
        ? colors.textBadgeBg ?? lightGreys.KPIBackground
        : statusBadgeColors.Planned.bg,
    };
  };

  const sortedData = useMemo((): AggregatedDataItem[] => {
    if (!data) return [];

    const grouped = data.reduce<Record<string, AggregatedDataItem>>(
      (acc, item) => {
        const key = item.sub_theme;
        acc[key] = acc[key] || { ...item, topics: [] };

        acc[key].topics = Array.from(
          new Set<string>([
            ...acc[key].topics,
            item.topic_name || MESSAGES.NOT_AVAILABLE,
          ])
        );

        acc[key].description = Array.from(
          new Set<string>([
            ...(acc[key].description || MESSAGES.NOT_AVAILABLE)
              .split("\n\n")
              .filter(Boolean),
            item.description || MESSAGES.NOT_AVAILABLE,
          ])
        ).join("\n\n");

        acc[key].is_mapped = acc[key].is_mapped || item.is_mapped;
        return acc;
      },
      {}
    );

    return Object.values(grouped).sort(
      (a, b) =>
        a.meta_theme.localeCompare(b.meta_theme) ||
        Number(b.is_mapped) - Number(a.is_mapped)
    );
  }, [data]);

  const filteredData = useMemo(() => {
    return sortedData.filter((item) =>
      selectedMetaThemes.includes(item.meta_theme)
    );
  }, [data, selectedMetaThemes]);

  const groupedByMetaTheme: Record<EngagementType, AggregatedDataItem[]> =
    useMemo(() => {
      return sortedData.reduce<Record<EngagementType, AggregatedDataItem[]>>(
        (acc, item) => {
          const key = item.meta_theme;
          acc[key] = acc[key] || [];
          acc[key].push(item);
          return acc;
        },
        {} as Record<EngagementType, AggregatedDataItem[]>
      );
    }, [sortedData]);

  const badgesStack: AvatarStackProps = useMemo(() => {
    return getBadges(filteredData);
  }, [filteredData]);

  return (
    <ElementContainer w="100%" flexDirection="column" ai="flex-start">
      <RowContainer ml={20} jc="flex-start" mb={1} ai="center">
        <CompassParagraph fontSize={14} bold>
          Material Issues
        </CompassParagraph>
        {!isLoading ? (
          Object.entries(groupedByMetaTheme).map(([metaTheme]) => {
            const key = metaTheme as EngagementType;
            const { color, bg } = getMetaThemeTagProps(key, selectedMetaThemes);
            return (
              <CompassTag
                key={key}
                tagKey={key}
                color={color}
                bg={bg}
                displayValue={`${formatReadable(key)} (${
                  groupedByMetaTheme[key].length || 0
                })`}
                isClickable={true}
                handleClick={(): void => handleSelectMetaTheme(key)}
              />
            );
          })
        ) : (
          <ElementContainer ml={-12}>
            {[1, 2, 3].map((_, idx) => (
              <LoadingTableCell key={`loadingmetatheme-${idx}`} dark />
            ))}
          </ElementContainer>
        )}
        <ElementContainer ai="center" bg="white" br={50} p={2} pt={2}>
          <AvatarStack {...badgesStack} />
        </ElementContainer>
      </RowContainer>

      <RowContainer>
        <Card h="auto" m="0 20px" p="8px" isLoading={isLoading}>
          <ElementContainer>
            <BadgeCardContainer>
              {!isLoading
                ? filteredData.map((item) => (
                    <BadgeCard
                      key={item.sub_theme}
                      title={formatReadable(item.sub_theme)}
                      subtitle={formatReadable(item.theme)}
                      isMapped={!!item.is_mapped}
                      type={item.meta_theme}
                      description={item.description}
                      topics={item.topics}
                    />
                  ))
                : Array.from({ length: 7 }).map((_, idx) => (
                    <div
                      key={`loadingcontainer-${idx}`}
                      style={{
                        height: "75px",
                        display: "flex",
                        alignItems: "center",
                        marginRight: "10px",
                      }}
                    >
                      <LoadingTableCell
                        key={`loadingtablecell-${idx}`}
                        height="60px"
                        width="150px"
                      />
                    </div>
                  ))}
            </BadgeCardContainer>
          </ElementContainer>
        </Card>
      </RowContainer>
    </ElementContainer>
  );
};

type BadgeCardsProps = {
  companyId: string | null;
};
